import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Chip,
  Avatar,
  Card,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useToast } from '../../contexts/ToastContext';
import ModernButton from '../common/ModernButton';
import ModernDataGrid from '../common/ModernDataGrid';
import { ConfirmDialog } from '../dialogs';

const GenericListPage = ({
  // Required props
  title,
  icon,
  columns,
  fetchData,
  deleteItem,
  createPath,
  editPath,

  // Optional props
  subtitle,
  entityName = 'item',
  getRowId = (row) => row._id,

  // Tab filtering (optional)
  tabs,
  filterKey,
  filterValues,

  // Extra actions (optional)
  extraActions,
  viewPath,
  onView,

  // Custom rendering (optional)
  renderStats,
  renderExtraButtons,
  customColumns,
  transformData,
}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const navigate = useNavigate();
  const { showToast } = useToast();

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetchData();
      const rawData = response.data || response;
      setData(transformData ? transformData(rawData) : rawData);
    } catch (error) {
      console.error(`Failed to fetch ${entityName}s:`, error);
      showToast(`Failed to load ${entityName}s`, 'error');
    } finally {
      setLoading(false);
    }
  }, [fetchData, entityName, showToast, transformData]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCreate = () => navigate(createPath);

  const handleEdit = (id) => {
    const path = typeof editPath === 'function' ? editPath(id) : `${editPath}/${id}/edit`;
    navigate(path);
  };

  const handleView = (row) => {
    if (onView) {
      onView(row);
    } else if (viewPath) {
      const path = typeof viewPath === 'function' ? viewPath(row) : `${viewPath}/${row._id}`;
      navigate(path);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await deleteItem(deleteTarget);
      setData((prev) => prev.filter((item) => getRowId(item) !== deleteTarget));
      showToast(`${entityName} deleted successfully!`, 'success');
    } catch (error) {
      console.error(`Failed to delete ${entityName}:`, error);
      showToast(`Failed to delete ${entityName}`, 'error');
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  const getFilteredData = () => {
    if (!tabs || !filterKey || !filterValues) return data;
    const filterValue = filterValues[activeTab];
    if (filterValue === undefined || filterValue === null) return data;
    return data.filter((item) => item[filterKey] === filterValue);
  };

  const filteredData = getFilteredData();

  const defaultActionColumn = {
    field: 'actions',
    headerName: 'Actions',
    sortable: false,
    flex: 1,
    minWidth: 150,
    renderCell: (params) => (
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        <Tooltip title="Edit">
          <IconButton
            onClick={() => handleEdit(getRowId(params.row))}
            size="small"
            sx={{ color: 'primary.main', '&:hover': { backgroundColor: 'primary.50' } }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {(viewPath || onView) && (
          <Tooltip title="View">
            <IconButton
              onClick={() => handleView(params.row)}
              size="small"
              sx={{ color: 'info.main', '&:hover': { backgroundColor: 'info.50' } }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {extraActions && extraActions(params.row)}
        <Tooltip title="Delete">
          <IconButton
            onClick={() => setDeleteTarget(getRowId(params.row))}
            size="small"
            sx={{ color: 'error.main', '&:hover': { backgroundColor: 'error.50' } }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    ),
  };

  const gridColumns = customColumns
    ? customColumns(handleEdit, handleView, setDeleteTarget, getRowId)
    : [...columns, defaultActionColumn];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar sx={{ backgroundColor: 'primary.main', width: 48, height: 48, mr: 2 }}>
            {icon}
          </Avatar>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary' }}>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                {subtitle}
              </Typography>
            )}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <ModernButton onClick={handleCreate} startIcon={<AddIcon />} variant="contained">
              Create New {entityName}
            </ModernButton>
            {renderExtraButtons && renderExtraButtons()}
          </Box>

          {renderStats ? (
            renderStats(data)
          ) : tabs ? (
            <Box sx={{ display: 'flex', gap: 1 }}>
              {tabs.map((tab, index) => {
                const count = filterValues && filterValues[index] != null
                  ? data.filter((item) => item[filterKey] === filterValues[index]).length
                  : data.length;
                return (
                  <Chip
                    key={tab.label}
                    label={`${count} ${tab.label}`}
                    color={tab.color || 'default'}
                    variant="outlined"
                    sx={{ fontWeight: 500 }}
                  />
                );
              })}
            </Box>
          ) : (
            <Chip label={`${data.length} Total`} variant="outlined" sx={{ fontWeight: 500 }} />
          )}
        </Box>
      </Box>

      {/* Tabs */}
      {tabs && (
        <Card sx={{ mb: 3, borderRadius: 3 }}>
          <Tabs
            value={activeTab}
            onChange={(e, v) => setActiveTab(v)}
            sx={{
              px: 3,
              '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '1rem' },
            }}
          >
            {tabs.map((tab, index) => {
              const count = filterValues && filterValues[index] != null
                ? data.filter((item) => item[filterKey] === filterValues[index]).length
                : data.length;
              return (
                <Tab
                  key={tab.label}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography>{tab.label}</Typography>
                      <Chip label={count} size="small" color={tab.color || 'default'} />
                    </Box>
                  }
                />
              );
            })}
          </Tabs>
        </Card>
      )}

      {/* Data Grid */}
      <ModernDataGrid rows={filteredData} getRowId={getRowId} columns={gridColumns} />

      {/* Delete Dialog */}
      <ConfirmDialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Confirm Deletion"
        message={`Are you sure you want to delete this ${entityName}? This action cannot be undone.`}
        confirmText="Delete"
        confirmColor="error"
        loading={deleting}
      />
    </Box>
  );
};

export default GenericListPage;
